export type AnnouncementDisplayMode = 'auto' | 'image_only' | 'text_only' | 'image_text'
export type AnnouncementLinkStyle = 'button' | 'text' | 'none'

export type AnnouncementDisplayInput = {
  title?: string | null
  content?: string | null
  imageUrl?: string | null
  displayMode?: AnnouncementDisplayMode | string | null
  linkUrl?: string | null
  linkOpenInNewTab?: boolean | null
  linkLabel?: string | null
  linkStyle?: AnnouncementLinkStyle | string | null
  imageClickable?: boolean | null
}

function hasPlainText(value?: string | null): boolean {
  return Boolean(String(value || '').trim())
}

export function resolveAnnouncementDisplayMode(input: AnnouncementDisplayInput): AnnouncementDisplayMode {
  const mode = String(input.displayMode || 'auto').trim() as AnnouncementDisplayMode
  if (mode === 'image_only' || mode === 'text_only' || mode === 'image_text') return mode

  const hasImage = Boolean(input.imageUrl)
  const hasText = hasPlainText(input.title) || hasPlainText(input.content)
  if (hasImage && hasText) return 'image_text'
  if (hasImage) return 'image_only'
  return 'text_only'
}

export function sanitizeAnnouncementLinkUrl(raw?: string | null): string | null {
  const value = String(raw || '').trim()
  if (!value) return null
  if (!/^https?:\/\//i.test(value)) return null
  return value
}

export function resolveAnnouncementLinkStyle(raw?: string | null): AnnouncementLinkStyle {
  const value = String(raw || 'button').trim()
  if (value === 'text' || value === 'none' || value === 'button') return value
  return 'button'
}

export function resolveAnnouncementLinkLabel(raw?: string | null): string {
  const label = String(raw || '').trim()
  return label || 'เปิดลิงก์'
}

export function isAnnouncementImageClickable(input: AnnouncementDisplayInput): boolean {
  return Boolean(sanitizeAnnouncementLinkUrl(input.linkUrl)) && input.imageClickable !== false
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function buildAnnouncementSwalHtml(options: {
  announcement: AnnouncementDisplayInput
  priorityLabel: string
  accentColor: string
  accentBadgeBg: string
  escapeHtml: (value: string) => string
  formatContentHtml: (value: string) => string
}): string {
  const { announcement, priorityLabel, accentColor, accentBadgeBg, escapeHtml, formatContentHtml } = options
  const mode = resolveAnnouncementDisplayMode(announcement)
  const linkUrl = sanitizeAnnouncementLinkUrl(announcement.linkUrl)
  const openInNewTab = announcement.linkOpenInNewTab !== false
  const linkStyle = resolveAnnouncementLinkStyle(announcement.linkStyle)
  const linkLabel = resolveAnnouncementLinkLabel(announcement.linkLabel)
  const imageClickable = isAnnouncementImageClickable(announcement)
  const title = String(announcement.title || '').trim()
  const content = String(announcement.content || '')
  const hasTitle = Boolean(title)
  const hasContent = Boolean(content.trim())
  const showImage = mode !== 'text_only' && Boolean(announcement.imageUrl)
  const showText = mode !== 'image_only' && (hasTitle || hasContent)
  const imageOnly = mode === 'image_only' && showImage

  const closeOnImage =
    'border:1px solid rgba(255,255,255,0.82);background:rgba(15,23,42,0.58);color:#fff;'
  const closeOnLight =
    'border:1px solid rgba(15,23,42,0.12);background:rgba(255,255,255,0.94);color:#0f172a;'
  const closeTone = showImage ? closeOnImage : closeOnLight

  const closeButton = (extraStyle = '') => `
    <button
      id="announcement-close-button"
      type="button"
      aria-label="Close announcement"
      style="position:absolute;top:14px;right:14px;z-index:5;display:flex;align-items:center;justify-content:center;width:40px;height:40px;border-radius:999px;cursor:pointer;font-size:24px;font-weight:400;line-height:1;backdrop-filter:blur(10px);${closeTone}${extraStyle}"
    >×</button>
  `

  const imgTag = showImage
    ? `<img
        src="${escapeHtml(String(announcement.imageUrl))}"
        alt="${escapeHtml(title || 'announcement')}"
        style="display:block;width:100%;max-height:${imageOnly ? 'min(78vh, 640px)' : 'min(56vh, 520px)'};object-fit:contain;margin:0 auto;background:#0f172a;"
        onerror="this.parentElement.style.display='none'"
      />`
    : ''

  const linkedImage = showImage
    ? imageClickable && linkUrl
      ? `<a href="${escapeAttr(linkUrl)}" ${openInNewTab ? 'target="_blank" rel="noopener noreferrer"' : ''} style="display:block;line-height:0;">${imgTag}</a>`
      : imgTag
    : ''

  const imageBlock = showImage
    ? `<div style="position:relative;background:#0f172a;">${linkedImage}${closeButton()}</div>`
    : closeButton('top:16px;right:16px;')

  let linkCta = ''
  if (linkUrl && linkStyle !== 'none') {
    if (linkStyle === 'text') {
      linkCta = `<a
        id="announcement-link-button"
        href="${escapeAttr(linkUrl)}"
        ${openInNewTab ? 'target="_blank" rel="noopener noreferrer"' : ''}
        style="display:inline-flex;align-items:center;justify-content:center;padding:8px 4px;color:${accentColor};font-size:15px;font-weight:700;text-decoration:underline;text-underline-offset:3px;cursor:pointer;"
      >${escapeHtml(linkLabel)}</a>`
    } else {
      linkCta = `<a
        id="announcement-link-button"
        href="${escapeAttr(linkUrl)}"
        ${openInNewTab ? 'target="_blank" rel="noopener noreferrer"' : ''}
        style="min-width:148px;display:inline-flex;align-items:center;justify-content:center;border:1px solid color-mix(in srgb, ${accentColor} 35%, #cbd5e1);border-radius:14px;padding:12px 20px;background:#fff;color:${accentColor};font-size:15px;font-weight:700;text-decoration:none;cursor:pointer;"
      >${escapeHtml(linkLabel)}</a>`
    }
  }

  const ackButton = `
    <button
      id="announcement-acknowledge-button"
      type="button"
      style="min-width:148px;border:none;border-radius:14px;padding:13px 24px;background:${accentColor};color:#fff;font-size:16px;font-weight:700;cursor:pointer;box-shadow:0 16px 32px ${accentBadgeBg};"
    >รับทราบ</button>
  `

  if (imageOnly) {
    return `
      <div style="position:relative;overflow:hidden;border-radius:26px;background:#0f172a;text-align:left;border:1px solid rgba(125,211,252,0.35);box-shadow:0 28px 80px rgba(2,6,23,0.42);">
        ${imageBlock}
        <div style="padding:16px 20px 20px;background:#fff;display:flex;flex-wrap:wrap;gap:10px;justify-content:center;align-items:center;">
          ${linkCta}
          ${ackButton}
        </div>
      </div>
    `
  }

  return `
    <div style="position:relative;overflow:hidden;border-radius:26px;background:#fff;text-align:left;border:1px solid rgba(125,211,252,0.35);box-shadow:0 28px 80px rgba(2,6,23,0.42),0 0 0 1px color-mix(in srgb, var(--htd-primary) 8%, transparent);">
      ${imageBlock}
      <div style="padding:${showImage ? '22px 24px 24px' : '24px'};">
        <div style="display:inline-flex;align-items:center;gap:8px;padding:6px 12px;border-radius:999px;background:${accentBadgeBg};color:${accentColor};font-size:12px;font-weight:700;letter-spacing:0.02em;">
          ${escapeHtml(priorityLabel)}
        </div>
        ${
          showText && hasTitle
            ? `<div style="margin-top:14px;color:#0f172a;font-size:31px;line-height:1.18;font-weight:800;letter-spacing:-0.02em;word-break:break-word;">${escapeHtml(title)}</div>`
            : ''
        }
        ${
          showText && hasContent
            ? `<div style="margin-top:12px;color:#475569;font-size:15px;line-height:1.85;word-break:break-word;">${formatContentHtml(content)}</div>`
            : ''
        }
        <div style="margin-top:22px;display:flex;flex-wrap:wrap;gap:10px;justify-content:center;align-items:center;">
          ${linkCta}
          ${ackButton}
        </div>
      </div>
    </div>
  `
}
